import { useState, type ReactNode } from 'react'

import { Button, Input, Select } from './components/Field'
import { LabelChip, LabelSwatch } from './components/IssueBadges'
import { Popover, PopoverItem } from './components/Popover'
import {
  ISSUE_TYPES,
  PRIORITY_LABELS,
  STATUS_LABELS,
  TASK_PRIORITIES,
  TASK_STATUSES,
  TYPE_LABELS,
} from './filters'
import { cn } from './lib/ui'
import type { IssueType, Label, LabelView, MemberRow, SprintView, TaskPriority, TaskStatus } from './types'

/** One label/control pair in the issue sidebar. */
export function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-2 py-1">
      <span className="text-xs font-medium text-ink-500">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

export function StatusSelect({
  value,
  onChange,
  disabled,
}: {
  value: TaskStatus
  onChange: (next: TaskStatus) => void
  disabled?: boolean
}) {
  return (
    <Select
      aria-label="Status"
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value as TaskStatus)}
      className="py-1 text-xs"
    >
      {TASK_STATUSES.map((status) => (
        <option key={status} value={status}>
          {STATUS_LABELS[status]}
        </option>
      ))}
    </Select>
  )
}

export function TypeSelect({
  value,
  onChange,
  disabled,
}: {
  value: IssueType
  onChange: (next: IssueType) => void
  disabled?: boolean
}) {
  return (
    <Select
      aria-label="Issue type"
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value as IssueType)}
      className="py-1 text-xs"
    >
      {ISSUE_TYPES.map((type) => (
        <option key={type} value={type}>
          {TYPE_LABELS[type]}
        </option>
      ))}
    </Select>
  )
}

export function PrioritySelect({
  value,
  onChange,
  disabled,
}: {
  value: TaskPriority
  onChange: (next: TaskPriority) => void
  disabled?: boolean
}) {
  return (
    <Select
      aria-label="Priority"
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value as TaskPriority)}
      className="py-1 text-xs"
    >
      {TASK_PRIORITIES.map((priority) => (
        <option key={priority} value={priority}>
          {PRIORITY_LABELS[priority]}
        </option>
      ))}
    </Select>
  )
}

export function AssigneeSelect({
  value,
  members,
  onChange,
  disabled,
}: {
  value: number | null | undefined
  members: MemberRow[]
  onChange: (next: number | null) => void
  disabled?: boolean
}) {
  return (
    <Select
      aria-label="Assignee"
      value={value ? String(value) : ''}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value ? Number(event.target.value) : null)}
      className="py-1 text-xs"
    >
      <option value="">Unassigned</option>
      {members.map((member) => (
        <option key={member.user_id} value={String(member.user_id)}>
          {member.user?.name ?? `User ${member.user_id}`}
        </option>
      ))}
    </Select>
  )
}

export function SprintSelect({
  value,
  sprints,
  onChange,
  disabled,
}: {
  value: string | null | undefined
  sprints: SprintView[]
  onChange: (next: string | null) => void
  disabled?: boolean
}) {
  return (
    <Select
      aria-label="Sprint"
      value={value ?? ''}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value || null)}
      className="py-1 text-xs"
    >
      <option value="">Backlog</option>
      {sprints.map((sprint) => (
        <option key={sprint.id} value={sprint.id}>
          {sprint.name}
          {sprint.status === 'active' ? ' · active' : ''}
        </option>
      ))}
    </Select>
  )
}

/**
 * An input that commits on blur or Enter and reverts on Escape.
 *
 * Committing on every keystroke would write an activity row per character; the
 * server also skips a write entirely when nothing is dirty, so an unchanged
 * value costs nothing here either.
 */
export function CommitInput({
  value,
  onCommit,
  disabled,
  placeholder,
  type = 'text',
  className,
  ariaLabel,
}: {
  value: string
  onCommit: (next: string) => void
  disabled?: boolean
  placeholder?: string
  type?: 'text' | 'date' | 'number'
  className?: string
  ariaLabel: string
}) {
  const [draft, setDraft] = useState(value)
  const [focused, setFocused] = useState(false)

  // While the field is not focused it mirrors the server's value, so a change
  // made elsewhere (an agent, another tab) shows up here.
  if (!focused && draft !== value) setDraft(value)

  const commit = () => {
    if (draft !== value) onCommit(draft)
  }

  return (
    <Input
      aria-label={ariaLabel}
      type={type}
      value={draft}
      disabled={disabled}
      placeholder={placeholder}
      className={cn('py-1 text-xs', className)}
      onFocus={() => setFocused(true)}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        setFocused(false)
        commit()
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault()
          commit()
          event.currentTarget.blur()
        }
        if (event.key === 'Escape') {
          setDraft(value)
          event.currentTarget.blur()
        }
      }}
    />
  )
}

const LABEL_COLORS = [
  'slate', 'red', 'orange', 'amber', 'yellow', 'lime', 'green', 'teal',
  'cyan', 'blue', 'indigo', 'violet', 'purple', 'pink', 'rose',
]

/**
 * The label picker. Ticking an existing label sends its id; typing a new name
 * sends the name, and the server creates the label inside the same transaction
 * that attaches it — so there is no window where a label exists but is unused.
 */
export function LabelPicker({
  applied,
  available,
  onChange,
  disabled,
}: {
  applied: Label[]
  available: LabelView[]
  onChange: (labels: string[]) => void
  disabled?: boolean
}) {
  const [draft, setDraft] = useState('')
  const appliedIDs = applied.map((label) => label.id)

  const toggle = (id: string) => {
    onChange(appliedIDs.includes(id) ? appliedIDs.filter((entry) => entry !== id) : [...appliedIDs, id])
  }

  const addNew = () => {
    const name = draft.trim()
    if (!name) return
    onChange([...appliedIDs, name])
    setDraft('')
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {applied.map((label) => (
        <LabelChip
          key={label.id}
          label={label}
          onRemove={disabled ? undefined : () => toggle(label.id)}
        />
      ))}

      {!disabled && (
        <Popover
          label="Choose labels"
          width="w-60"
          trigger={({ toggle: open }) => (
            <button
              type="button"
              onClick={open}
              className="rounded border border-dashed border-ink-300 px-1.5 py-0.5 text-[11px] text-ink-500 transition hover:border-ink-400 hover:text-ink-700 dark:border-ink-700 dark:hover:text-ink-200"
            >
              + Label
            </button>
          )}
        >
          {() => (
            <>
              {available.map((label) => (
                <PopoverItem
                  key={label.id}
                  selected={appliedIDs.includes(label.id)}
                  onClick={() => toggle(label.id)}
                >
                  <span className="flex items-center gap-2">
                    <LabelSwatch color={label.color} />
                    <span className="truncate">{label.name}</span>
                  </span>
                </PopoverItem>
              ))}
              <div className="mt-1 flex gap-1 border-t border-ink-200 px-1 pt-1.5 dark:border-ink-800">
                <Input
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      addNew()
                    }
                  }}
                  placeholder="New label"
                  className="py-1 text-xs"
                />
                <Button size="sm" onClick={addNew} disabled={!draft.trim()}>
                  Add
                </Button>
              </div>
              <p className="px-2 pt-1 text-[10px] text-ink-400">
                Colours: {LABEL_COLORS.slice(0, 5).join(', ')}…
              </p>
            </>
          )}
        </Popover>
      )}
    </div>
  )
}
